/**
 * CAP-API-1a-group-1 — group page slot context from overview (DEF-FE-3b-001 partial).
 */

import { fleetPollTruncatedHtml } from "./fleet-list-paginated.js";
import {
  buildOverviewSlotsFromApiResponse,
  hasUsableOverviewGroupSlots,
} from "./overview-group-slots.js";
import { isOverviewDeviceOnline } from "./overview-group-slot-helpers.js";

/**
 * @param {object | null | undefined} ov
 * @param {string} groupKey
 * @param {string} tenantOwner
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string }} ctx
 * @returns {object | null}
 */
export function findGroupPageOverviewSlot(ov, groupKey, tenantOwner, ctx) {
  if (!hasUsableOverviewGroupSlots(ov)) return null;
  const slots = buildOverviewSlotsFromApiResponse(ov, ctx);
  if (!Array.isArray(slots)) return null;
  const mk = ctx.groupCardMetaKey(groupKey, tenantOwner);
  if (!mk) return null;
  return slots.find((s) => String(s.metaKey || "") === mk) || null;
}

/**
 * @param {object | null} slot
 * @param {object[]} rows
 * @returns {{ total: number, online: number, offline: number, fromServer: boolean }}
 */
export function groupPagePresenceCounts(slot, rows) {
  const list = Array.isArray(rows) ? rows : [];
  if (slot && slot.serverCounts) {
    const total = Number(slot.serverCounts.total || 0);
    const online = Number(slot.serverCounts.online || 0);
    return {
      total,
      online,
      offline: Math.max(0, total - online),
      fromServer: true,
    };
  }
  const online = list.filter((d) => isOverviewDeviceOnline(d)).length;
  return {
    total: list.length,
    online,
    offline: Math.max(0, list.length - online),
    fromServer: false,
  };
}

/**
 * Server-scoped group list still loading or capped (show load-more / trunc banner).
 *
 * @param {object | null} slot
 * @param {object[]} rows
 * @param {object | null | undefined} [list]
 * @returns {boolean}
 */
export function groupPageSampleMismatch(slot, rows, list) {
  if (list && list.truncated) return true;
  if (list && list._legacyClientFilter) {
    const loaded = Array.isArray(rows) ? rows.length : 0;
    if (!slot || !slot.serverCounts) return false;
    const serverTotal = Number(slot.serverCounts.total || 0);
    return serverTotal > 0 && loaded < serverTotal;
  }
  return false;
}

/**
 * Overview server total exceeds visible rows after a complete group-scoped fetch (ACL partial).
 *
 * @param {object | null} slot
 * @param {object[]} rows
 * @param {object | null | undefined} [list]
 * @returns {boolean}
 */
export function groupPageAccessPartial(slot, rows, list) {
  if (list && list.truncated) return false;
  if (list && list._legacyClientFilter) return false;
  const loaded = Array.isArray(rows) ? rows.length : 0;
  if (!slot || !slot.serverCounts) return false;
  const serverTotal = Number(slot.serverCounts.total || 0);
  return serverTotal > 0 && loaded < serverTotal;
}

/**
 * @param {object | null} slot
 * @param {object[]} rows
 * @param {object | null | undefined} list
 * @param {string} groupKey
 * @param {string} tenantOwner
 * @param {string} [bannerId]
 * @returns {string}
 */
export function groupPageSampleMismatchHtml(slot, rows, list, groupKey, tenantOwner, bannerId = "grpFleetTruncBanner") {
  if (groupPageAccessPartial(slot, rows, list)) {
    const loaded = Array.isArray(rows) ? rows.length : 0;
    const serverTotal = slot && slot.serverCounts ? Number(slot.serverCounts.total || 0) : 0;
    return (
      `<p class="badge neutral" id="${bannerId}" data-group-access-partial="1" style="margin:8px 0">` +
      `You can view ${loaded} of ${serverTotal} boards in this group with your current access.` +
      ` Group alarms still apply to the full server group.` +
      `</p>`
    );
  }
  if (!groupPageSampleMismatch(slot, rows, list)) return "";
  const loaded = Array.isArray(rows) ? rows.length : 0;
  const serverTotal = slot && slot.serverCounts ? Number(slot.serverCounts.total || 0) : 0;
  const alarmNote = " Group alarms still apply to the full server group.";
  const detail = list && list._legacyClientFilter
    ? (serverTotal > 0
      ? `This page has ${loaded} of ${serverTotal} boards. Load more below.${alarmNote}`
      : `Board list incomplete. Load more below.${alarmNote}`)
    : `Large group — first ${loaded} boards loaded. Load more below.${alarmNote}`;
  const drill = groupKey
    ? ` Or use <a href="#/group/_all">All boards</a> for the full board list.`
    : "";
  return (
    `<p class="badge offline" id="${bannerId}" data-group-sample-mismatch="1" style="margin:8px 0">` +
    `${detail}${drill}` +
    `</p>`
  );
}

/**
 * @param {object | null | undefined} list
 * @param {object | null | undefined} ov
 * @param {string} groupKey
 * @param {string} tenantOwner
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string }} ctx
 * @param {string} [bannerId]
 */
export function groupPageFleetTruncatedHtml(list, ov, groupKey, tenantOwner, ctx, bannerId = "grpFleetTruncBanner") {
  const slot = findGroupPageOverviewSlot(ov, groupKey, tenantOwner, ctx);
  const rows = list && Array.isArray(list.items) ? list.items : [];
  const mismatchHtml = groupPageSampleMismatchHtml(slot, rows, list, groupKey, tenantOwner, bannerId);
  if (mismatchHtml) return mismatchHtml;
  if (slot) return "";
  return fleetPollTruncatedHtml(list, bannerId);
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {object | null | undefined} list
 * @param {object | null | undefined} ov
 * @param {string} groupKey
 * @param {string} tenantOwner
 * @param {{ me?: object | null, canonicalGroupKey: (v: unknown) => string, groupCardMetaKey: (gk: string, tenant?: string) => string }} ctx
 * @param {string} [bannerId]
 */
export function syncGroupPageFleetTruncBanner(view, list, ov, groupKey, tenantOwner, ctx, bannerId = "grpFleetTruncBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  const html = groupPageFleetTruncatedHtml(list, ov, groupKey, tenantOwner, ctx, bannerId);
  if (!html) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const anchor = view.querySelector("header.page-head, section.card, .page-head");
  if (anchor) anchor.insertAdjacentHTML("afterend", html);
  else view.insertAdjacentHTML("afterbegin", html);
}
